/* eslint-disable max-len */
import * as React from 'react';
import styled from '@emotion/styled';
import {Ref, FocusEvent} from 'react';

interface CheckBoxProps {
    alias: string;
    ariaLabel?: string;
    caption?: string;
    cardMargin?: boolean;
    checked?: boolean;
    className?: string;
    defaultChecked?: boolean;
    disabled?: boolean;
    e2e?: string;
    error?: string;
    errorPosition?: `above` | `below`;
    label?: string | React.JSX.Element;
    labelClass?: string;
    name: string;
    noMargin?: boolean;
    onChange?: (e) => void;
    onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
    ref?: Ref<unknown> | null;
    smallCheckbox?: boolean;
    subLabel?: string;
    value?: string;
}

export const CheckboxStyles = styled.div`
    label {
        &:after {
            background: 50% / 50% 50% no-repeat;
        }
    }

    input:checked ~ label::before {
        color: #fff;
        border-color: #00599c;
        background-color: #00599c;
    }

    input:checked ~ label::after {
        height: 1.5rem;
        width: 1.5rem;
        background-image: url('data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%278%27 height=%278%27 viewBox=%270 0 8 8%27%3e%3cpath fill=%27%23fff%27 d=%27M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z%27/%3e%3c/svg%3e');
    }
`;

export const CheckBoxComponent = (
    {
        alias,
        ariaLabel,
        caption,
        cardMargin = false,
        checked,
        className,
        defaultChecked,
        disabled,
        e2e,
        error = ``,
        errorPosition = `below`,
        label,
        labelClass,
        name,
        noMargin = false,
        onChange,
        onFocus,
        smallCheckbox = false,
        subLabel,
        value = ``,
    }: CheckBoxProps,
    ref,
) => (
    <CheckboxStyles>
        <div
            className={`${className ? ` ` + className : ``} ${
                disabled ? '' : ' tw-cursor-pointer hover:tw-cursor-pointer'
            } tw-relative tw-z-auto tw-min-h-[1.5rem] tw-pl-2 ${noMargin ? 'tw-mb-0' : 'tw-mb-4 md:tw-mb-0'}`}
        >
            {errorPosition === `above` && error && <label className="!tw-text-red-500 tw-inline-block tw-ml-2 tw-pl-1">{error}</label>}
            <input
                aria-label={ariaLabel}
                checked={checked}
                className={`!tw-z-auto tw-mt-[0.3rem] -tw-ml-5 tw-rounded-sm tw-absolute tw-left-0 tw-w-4 tw-h-5 tw-opacity-0 tw-p-0${
                    error ? ` !tw-border-red-500 tw-inline-block !tw-border-2` : ``
                }`}
                defaultChecked={defaultChecked}
                disabled={disabled}
                id={`${alias}_checkbox`}
                data-e2e={e2e}
                name={name}
                onChange={onChange}
                onFocus={onFocus}
                ref={ref}
                type="checkbox"
                value={value}
            />
            <label
                className={`tw-pr-0 tw-pb-1 !tw-pl-8 tw-text-base tw-relative tw-mb-0 tw-align-top before:tw-absolute before:tw-top-1 before:tw-block before:tw-delay-100 before:tw-ease-in-out before:tw-duration-200 before:tw-pointer-events-none before:tw-rounded-sm before:tw-content-[""] before:-tw-ml-10 after:-tw-ml-10 after:tw-rounded-sm after:tw-absolute after:tw-top-1 after:tw-block after:tw-content-[""] after:tw-transition-all after:tw-delay-100 after:tw-ease-in-out after:tw-duration-200 before:tw-border after:tw-border' ${
                    disabled
                        ? 'tw-cursor-not-allowed hover:tw-cursor-not-allowed before:tw-cursor-not-allowed after:tw-cursor-not-allowed before:tw-bg-gray-100 before:tw-border-gray-200'
                        : 'before:tw-bg-white'
                }${
                    smallCheckbox
                        ? ' !tw-pl-4 tw-pt-0.5 before:!tw-w-5 before:!tw-h-5 after:!tw-w-5 after:!tw-h-5'
                        : ' tw-pt-1.5 before:!tw-h-6 before:tw-w-6 after:!tw-w-6 after:!tw-h-6'
                }${label || cardMargin ? ' tw-ml-0' : ' tw-ml-6'}${labelClass ? ` ` + labelClass : ``}${
                    subLabel ? ' before:tw-top-[50%] before:tw-mt-[-11px] after:tw-top-[50%] after:tw-mt-[-11px]' : ''
                }${
                    checked && !disabled
                        ? ' check-background before:tw-text-white before:!tw-bg-blue-200 before:tw-border before:tw-border-blue-600'
                        : ''
                }`}
                htmlFor={`${alias}_checkbox`}
            >
                {label ? label : `\u00A0`}
                {subLabel && <div className="tw-text-sm tw-font-normal tw-leading-[1.3]">{subLabel}</div>}
            </label>
            {errorPosition === `below` && error && <label className="tw-text-red-500 tw-ml-2 tw-pl-1">{error}</label>}
            {caption && <div className="tw-ml-2 tw-pl-1 tw-text-sm tw-font-normal tw-leading-[1.3]">{caption}</div>}
        </div>
    </CheckboxStyles>
);
export const CheckBox = React.forwardRef(CheckBoxComponent);
